import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

export default function BillingPage() {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (searchParams.get('success') !== null) {
      setStatus('success');
    } else if (searchParams.get('cancel') !== null) {
      setStatus('cancelled');
    }

    // Fetch user data, retry if still trialing after checkout success
    const fetchUser = async (retries = 0) => {
      const userData = await api.getMe();
      setUser(userData);
      // If just returned from checkout but status hasn't updated yet, retry
      if (
        searchParams.get('success') !== null &&
        userData.subscription_status === 'trialing' &&
        retries < 5
      ) {
        setTimeout(() => fetchUser(retries + 1), 2000);
      }
    };
    fetchUser();
  }, [searchParams]);

  const isActive = user?.subscription_status === 'active';
  const currency = user?.currency || 'gbp';
  const symbol = currency === 'usd' ? '$' : '£';
  const earlyPrice = 20;

  const getTrialDaysLeft = () => {
    if (!user?.created_at || user?.subscription_status !== 'trialing') return null;
    const created = new Date(user.created_at);
    const trialEnd = new Date(created.getTime() + 14 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysLeft);
  };

  const trialDaysLeft = getTrialDaysLeft();

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const data = await api.createCheckout();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      alert('Failed to create checkout: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const data = await api.createPortalSession();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (err) {
      alert('Failed to open billing portal: ' + err.message);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Billing</h1>
        <p className="page-subtitle">Manage your subscription</p>
      </div>

      {status === 'success' && (
        <div className="card" style={{ padding: '20px', marginBottom: '20px', background: 'var(--green-light)', borderColor: 'var(--green)' }}>
          <strong style={{ color: 'var(--green)' }}>🎉 Subscription active!</strong>
          <p style={{ margin: '4px 0 0', fontSize: '14px' }}>Thank you for subscribing to StellarReply Pro.</p>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="card" style={{ padding: '20px', marginBottom: '20px', background: 'var(--yellow-light)', borderColor: 'var(--yellow)' }}>
          <strong style={{ color: 'var(--yellow)' }}>Checkout cancelled</strong>
          <p style={{ margin: '4px 0 0', fontSize: '14px' }}>No worries, you can subscribe whenever you're ready.</p>
        </div>
      )}

      <div className="billing-card card">
        <div className="billing-early-bird">
          🚀 Early adopter pricing, locked in forever
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div className="plan-name">StellarReply Pro</div>
          <span className={`subscription-badge ${isActive ? 'active' : 'trialing'}`}>
            {isActive ? 'Active' : trialDaysLeft !== null ? `Trial · ${trialDaysLeft} days left` : user?.subscription_status || 'Trial'}
          </span>
        </div>

        <div className="plan-price">
          {symbol}{earlyPrice}<span>/month</span>
        </div>

        <ul className="plan-features">
          <li>Unlimited AI-generated review responses</li>
          <li>Up to 5 business locations</li>
          <li>Manual review entry with AI-powered responses</li>
          <li>Review translation</li>
          <li>Analytics dashboard &amp; review insights</li>
          <li>Branded review request links</li>
        </ul>

        {isActive ? (
          <button
            className="btn btn-primary"
            onClick={handleManageSubscription}
            style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
          >
            Manage Subscription
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={handleSubscribe}
            disabled={loading}
            style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
          >
            {loading ? 'Setting up...' : `Subscribe Now · ${symbol}${earlyPrice}/month`}
          </button>
        )}
        <p style={{ fontSize: '12px', color: 'var(--text-light)', textAlign: 'center', marginTop: '12px' }}>
          Powered by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
